import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Calendar, User, Trash2, Plus, Pencil } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/hiring`;

const PayRaisesManagement = () => {
  const [payRaises, setPayRaises] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRaise, setEditingRaise] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    effective_date: '',
    raise_percentage: 0,
    notes: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [raisesResponse, employeesResponse] = await Promise.all([
        axios.get(`${API}/pay-raises`),
        axios.get(`${API}/employees`)
      ]);
      setPayRaises(raisesResponse.data);
      setEmployees(employeesResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRaise) {
        // Update existing raise
        await axios.put(`${API}/pay-raises/${editingRaise.id}`, null, {
          params: {
            effective_date: formData.effective_date,
            raise_percentage: formData.raise_percentage,
            notes: formData.notes
          }
        });
        toast({ title: 'Success', description: 'Pay raise updated successfully' });
      } else {
        // Create new raise
        await axios.post(`${API}/pay-raises`, formData);
        
        // Also update employee with current raise
        await axios.put(`${API}/employees/${formData.employee_id}`, {
          current_raise_percentage: formData.raise_percentage
        });
        toast({ title: 'Success', description: 'Pay raise added successfully' });
      }
      
      setIsDialogOpen(false);
      setEditingRaise(null);
      setFormData({ employee_id: '', effective_date: '', raise_percentage: 0, notes: '' });
      fetchData();
    } catch (error) {
      console.error('Error saving pay raise:', error);
      toast({ title: 'Error', description: 'Failed to save pay raise', variant: 'destructive' });
    }
  };

  const handleEdit = (raise) => {
    setEditingRaise(raise);
    setFormData({
      employee_id: raise.employee_id,
      effective_date: new Date(raise.effective_date).toISOString().split('T')[0],
      raise_percentage: raise.raise_percentage,
      notes: raise.notes || ''
    });
    setIsDialogOpen(true);
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? employee.name : 'Unknown';
  };

  const getEmployeeDetails = (employeeId) => {
    return employees.find(e => e.id === employeeId);
  };

  const handleDeleteRaise = async (raiseId) => {
    if (!window.confirm('Are you sure you want to delete this pay raise?')) return;
    
    try {
      await axios.delete(`${API}/pay-raises/${raiseId}`);
      toast({ title: 'Success', description: 'Pay raise deleted successfully' });
      fetchData();
    } catch (error) {
      console.error('Error deleting raise:', error);
      toast({ title: 'Error', description: 'Failed to delete pay raise', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Group raises by year and sort
  const raisesByYear = payRaises.reduce((acc, raise) => {
    const year = new Date(raise.effective_date).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(raise);
    return acc;
  }, {});

  // Calculate total raise amounts by employee
  const employeeRaisesSummary = employees.map(emp => {
    const empRaises = payRaises.filter(r => r.employee_id === emp.id);
    const totalRaise = empRaises.reduce((sum, r) => sum + (emp.salary_usd * r.raise_percentage), 0);
    const activeRaise = empRaises
      .filter(r => new Date(r.effective_date) <= new Date())
      .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date))[0];
    
    return {
      ...emp,
      totalRaises: empRaises.length,
      totalRaiseAmount: totalRaise,
      activeRaisePercentage: activeRaise ? activeRaise.raise_percentage : 0
    };
  }).filter(emp => emp.totalRaises > 0);

  return (
    <div className='p-6 space-y-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen'>
      <div className='flex justify-between items-center'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>Pay Raises Management</h1>
          <p className='text-gray-600 mt-1'>Track and manage salary increases</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className='shadow-md bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'>
              <Plus className='mr-2 h-4 w-4' />
              Add Pay Raise
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className='text-2xl'>{editingRaise ? 'Edit Pay Raise' : 'Add Pay Raise'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='employee'>Employee *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                  required
                  disabled={editingRaise !== null}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select employee' />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} - {emp.job_title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='raise_percentage'>Raise Percentage (e.g., 0.1 for 10%) *</Label>
                <Input
                  id='raise_percentage'
                  type='number'
                  step='0.01'
                  value={formData.raise_percentage}
                  onChange={(e) => setFormData({ ...formData, raise_percentage: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='effective_date'>Effective Date *</Label>
                <Input
                  id='effective_date'
                  type='date'
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='notes'>Notes</Label>
                <Input
                  id='notes'
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder='e.g., Annual performance raise'
                />
              </div>
              <DialogFooter>
                <Button type='button' variant='outline' onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type='submit' className='bg-gradient-to-r from-green-600 to-green-700'>
                  {editingRaise ? 'Update Raise' : 'Add Raise'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        <Card className='shadow-md border-l-4 border-l-green-500'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-sm font-medium text-gray-600'>Total Raises</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-bold text-green-600'>{payRaises.length}</div>
          </CardContent>
        </Card>

        <Card className='shadow-md border-l-4 border-l-blue-500'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-sm font-medium text-gray-600'>Employees with Raises</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-bold text-[#C9A64A]'>{employeeRaisesSummary.length}</div>
          </CardContent>
        </Card>

        <Card className='shadow-md border-l-4 border-l-purple-500'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-sm font-medium text-gray-600'>Avg Raise %</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-bold text-purple-600'>
              {payRaises.length > 0 
                ? (payRaises.reduce((sum, r) => sum + r.raise_percentage, 0) / payRaises.length * 100).toFixed(1)
                : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Raises Summary */}
      <Card className='shadow-md'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <User className='h-5 w-5' />
            Employee Raises Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className='bg-slate-50'>
                <TableHead>Employee</TableHead>
                <TableHead>Current Salary (USD)</TableHead>
                <TableHead>Active Raise %</TableHead>
                <TableHead>Total Raises</TableHead>
                <TableHead>Total Increase Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employeeRaisesSummary.map((emp) => (
                <TableRow key={emp.id} className='hover:bg-slate-50'>
                  <TableCell className='font-medium'>{emp.name}</TableCell>
                  <TableCell>${emp.salary_usd.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant='default' className='bg-green-600'>
                      {(emp.activeRaisePercentage * 100).toFixed(1)}%
                    </Badge>
                  </TableCell>
                  <TableCell>{emp.totalRaises}</TableCell>
                  <TableCell className='font-semibold text-green-600'>
                    ${emp.totalRaiseAmount.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Raises by Year */}
      {Object.keys(raisesByYear).sort((a, b) => b - a).map(year => (
        <Card key={year} className='shadow-md'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Calendar className='h-5 w-5' />
              Pay Raises in {year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className='bg-slate-50'>
                  <TableHead>Employee</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Effective Date</TableHead>
                  <TableHead>Raise %</TableHead>
                  <TableHead>Estimated Annual Impact</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {raisesByYear[year].map((raise) => {
                  const employee = getEmployeeDetails(raise.employee_id);
                  const annualImpact = employee ? employee.salary_usd * raise.raise_percentage : 0;
                  return (
                    <TableRow key={raise.id} className='hover:bg-slate-50'>
                      <TableCell className='font-medium'>{getEmployeeName(raise.employee_id)}</TableCell>
                      <TableCell>
                        <Badge variant='outline'>{employee?.team || '-'}</Badge>
                      </TableCell>
                      <TableCell>{new Date(raise.effective_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Badge variant='default' className='bg-green-600'>
                          {(raise.raise_percentage * 100).toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className='font-semibold text-green-600'>
                        ${annualImpact.toLocaleString()}
                      </TableCell>
                      <TableCell className='text-sm text-gray-600'>{raise.notes || '-'}</TableCell>
                      <TableCell>
                        <div className='flex gap-2'>
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => handleEdit(raise)}
                            title='Edit raise'
                          >
                            <Pencil className='h-4 w-4 text-[#C9A64A]' />
                          </Button>
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => handleDeleteRaise(raise.id)}
                            title='Delete raise'
                          >
                            <Trash2 className='h-4 w-4 text-red-600' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      {payRaises.length === 0 && (
        <Card className='shadow-md'>
          <CardContent className='py-12 text-center text-gray-500'>
            <TrendingUp className='h-12 w-12 mx-auto mb-4 opacity-30' />
            <p className='text-lg font-medium'>No pay raises recorded yet.</p>
            <p className='text-sm mt-2'>Click 'Add Pay Raise' button above to create one.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PayRaisesManagement;
